import {useMemo} from 'react';
import {Alert, AlertTitle, Button, Calendar, Card} from '@ezcater/tapas';
import {format, parse, parseISO} from 'date-fns';
import {Form, useFormikContext} from 'formik';
import {capitalize} from 'lodash-es';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import EventBarTimePicker from '@/components/global/layout/EventBar/EventBarTimePicker';
import {type TrackEvent} from '@/components/global/layout/EventBar/types';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {FulfillmentDetailStrategy} from '@/graphql/types';
import useIsAdmin from '@/hooks/useIsAdmin';
import useMediaQuery from '@/hooks/useMediaQuery';
import {convertDateDateToString} from '@/utils/dateFormat';
import {FulfillmentDetailFormProps} from './EventBar';

type DateTimeProps = {
  trackEvent: TrackEvent;
};

const DateTime: React.FC<DateTimeProps> = ({trackEvent}) => {
  const isAdmin = useIsAdmin();
  const {isOnMenuPage, isOnSearchPage} = useStoreNextRouter();
  const {status, setFieldValue, values, isSubmitting} =
    useFormikContext<FulfillmentDetailFormProps>();
  const {eventTime, eventDate, eventOrderType} = values;
  const parsedDate = useMemo(() => (eventDate == null ? null : parseISO(eventDate)), [eventDate]);
  const isTakeout = eventOrderType === FulfillmentDetailStrategy.Takeout;
  const headerStrategy = capitalize(isTakeout ? 'Pickup' : FulfillmentDetailStrategy.Delivery);
  const isLandscape = useMediaQuery('(max-height: 900px)');
  const minCalendarDate = isAdmin ? new Date('2000-01-01T00:00:00') : new Date();
  const {t} = useTranslation('app-bar');

  const cta = isOnMenuPage
    ? t('eventBar.eventDate.cta.menu')
    : isOnSearchPage
      ? t('eventBar.eventDate.cta.search')
      : t('eventBar.eventDate.cta.accountPage');

  return (
    <Form data-testid="event-bar-date-time-form">
      {status?.error && (
        <Alert className="w-[380px]" variant="critical">
          <AlertTitle>{status.error.message}</AlertTitle>
        </Alert>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-6 [@media(max-height:900px)]:flex-row">
          {/* Calendar's root is a <div>, which `htmlFor` cannot label — name the group that wraps
              the heading and the calendar instead. The day grid keeps react-day-picker's own
              month `aria-label`. */}
          <div aria-labelledby="event-bar-date-label" className="flex flex-col gap-2" role="group">
            <span className="font-bold" id="event-bar-date-label">
              {t('eventBar.eventDate.inputs.date', {strategy: headerStrategy})}
            </span>
            <Card className="w-[380px] rounded-xl p-0">
              <Calendar
                // Always render 6 week rows so the fields below don't shift by a row's height as
                // the user pages between months.
                fixedWeeks
                // `root: w-full` fills the card, which widens each day cell past `--cell-size` via its
                // `flex-1`. Tapas's day button is `w-full aspect-square`, so its height would follow that
                // stretched width and overflow the row's fixed height, overlapping the rows above and below.
                // `aspect-auto h-full` makes the button fill its cell instead, restoring the wide-ish day
                // cells the v7 inline DatePicker rendered here before the migration.
                classNames={{
                  root: 'w-full',
                  day_button: twJoin(
                    'aspect-auto h-full',
                    // Tapas's Button base contributes `hover:bg-peppercorn-100`, which outranks the
                    // selected day's `bg-green-700` on hover and flips it to grey. Keep it green, one
                    // step lighter, as the v7 DatePicker did.
                    'data-[selected-single=true]:hover:bg-green-600',
                    // react-day-picker marks the picked day `focused` however it was picked, and Tapas
                    // rings any `data-focused` day — so a mouse click leaves an outline behind. Suppress
                    // that ring, then put it back for `:focus-visible` only, so keyboard focus stays visible.
                    // The focus-visible pair carries both variants so it outranks the suppression above.
                    'group-data-[focused=true]/day:ring-0 group-data-[focused=true]/day:ring-offset-0',
                    'group-data-[focused=true]/day:focus-visible:ring-[3px]',
                    'group-data-[focused=true]/day:focus-visible:ring-offset-1',
                  ),
                }}
                defaultMonth={parsedDate ?? new Date()}
                // Bounds the selectable days without a `startMonth`, which would disable
                // month-back navigation and hide the leading outside-days.
                disabled={{before: minCalendarDate}}
                id="event-bar-date"
                mode="single"
                // Re-picking the selected day deselects it, which is how the field is cleared.
                onSelect={date => {
                  const nextDate = convertDateDateToString(date);
                  setFieldValue('eventDate', nextDate);
                  trackEvent('date-selected', {eventDate: nextDate});
                }}
                selected={parsedDate ?? undefined}
              />
            </Card>
          </div>

          <div className="flex w-[380px] flex-col gap-2">
            <label className="flex items-center gap-1 font-bold" htmlFor="event-bar-time">
              {t('eventBar.eventDate.inputs.time', {strategy: headerStrategy})}
            </label>

            {!isTakeout && (
              <div className="text-sm leading-tight [@media(max-height:900px)]:mb-1">
                {t('eventBar.eventDate.helperText')}
              </div>
            )}

            <EventBarTimePicker
              key={isLandscape ? 'landscape' : 'portrait'}
              allowDeselectTime
              className="w-[380px]"
              id="event-bar-time"
              onSelectTime={(time: string) => {
                const nextTime = time ? format(parse(time, 'h:mm a', new Date()), 'HH:mm:ss') : '';
                setFieldValue('eventTime', nextTime);
                trackEvent('time-selected', {eventTime: nextTime || null});
              }}
              selectedTime={
                eventTime ? format(parse(eventTime, 'HH:mm:ss', new Date()), 'h:mm a') : undefined
              }
              timesPerPage={isLandscape ? 20 : 12}
              initialShowIndex={isLandscape ? 20 : 24}
            />
          </div>
        </div>

        <Alert className="w-[380px] select-none [@media(max-height:900px)]:w-full" variant="info">
          <AlertTitle>{t('eventBar.eventDate.info')}</AlertTitle>
        </Alert>
      </div>

      <Button className="mt-4 w-full" loading={isSubmitting} type="submit">
        {cta}
      </Button>
    </Form>
  );
};

export default DateTime;
